import { useState } from "react";
import { Truck, Loader2, MapPin } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api, errMsg } from "../lib/api";

/**
 * Pincode → estimated delivery window. Origin pincode lives on the server.
 */
export const PincodeCheck = () => {
    const { t } = useLang();
    const [pin, setPin] = useState("");
    const [loading, setLoading] = useState(false);
    const [res, setRes] = useState(null);
    const [error, setError] = useState("");

    const check = async (e) => {
        e.preventDefault();
        setError("");
        setRes(null);
        if (!/^\d{6}$/.test(pin)) {
            setError(t({ en: "Please enter a valid 6-digit pincode.", te: "సరైన 6 అంకెల పిన్‌కోడ్ నమోదు చేయండి." }));
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.get(`/shipping/estimate?pincode=${pin}`);
            setRes(data);
        } catch (err) {
            setError(errMsg(err, t({ en: "Could not check right now.", te: "ఇప్పుడు తనిఖీ చేయలేకపోయాము." })));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mt-8 rounded-3xl border border-[color:var(--kp-ink)]/12 bg-[color:var(--kp-cream-2)]/60 p-5" data-testid="pincode-check">
            <div className="flex items-center gap-2 text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 mb-3">
                <MapPin size={12} />
                {t({ en: "Check delivery to your pincode", te: "మీ పిన్‌కోడ్‌కు డెలివరీ తనిఖీ" })}
            </div>

            <form onSubmit={check} className="flex gap-2">
                <input
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputMode="numeric"
                    placeholder="522612"
                    className="flex-1 min-w-0 rounded-full bg-[color:var(--kp-cream)] border border-[color:var(--kp-ink)]/20 px-4 py-3 text-sm tracking-mono-wide outline-none focus:border-[color:var(--kp-terracotta)]"
                    data-testid="pincode-input"
                />
                <button
                    type="submit"
                    disabled={loading}
                    className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[color:var(--kp-ink)] text-[color:var(--kp-cream)] px-5 py-3 text-[11px] tracking-mono-wide font-bold disabled:opacity-60"
                    data-testid="pincode-submit"
                >
                    {loading ? <Loader2 size={13} className="animate-spin" /> : <Truck size={13} />}
                    {t({ en: "CHECK", te: "తనిఖీ" })}
                </button>
            </form>

            {error && (
                <p className="mt-3 text-[12px] text-[color:var(--kp-red)]" data-testid="pincode-error">
                    {error}
                </p>
            )}

            {res && (
                <div className="mt-4 rounded-2xl bg-[color:var(--kp-cream)] border border-[color:var(--kp-banana)]/40 px-4 py-3" data-testid="pincode-result">
                    <div className="text-sm font-bold text-[color:var(--kp-ink)]">
                        {t({
                            en: `Estimated delivery: ${res.eta_from} – ${res.eta_to}`,
                            te: `అంచనా డెలివరీ: ${res.eta_from} – ${res.eta_to}`,
                        })}
                    </div>
                    <div className="text-[11px] text-[color:var(--kp-ink-soft)] mt-1" data-testid="pincode-zone">
                        {res.zone} · {t({ en: `${res.min_days}–${res.max_days} days after dispatch`, te: `డిస్పాచ్ తర్వాత ${res.min_days}–${res.max_days} రోజులు` })}
                    </div>
                    {!res.allows_250g && (
                        <div className="text-[11px] text-[color:var(--kp-red)] font-bold mt-2" data-testid="pincode-250g-warning">
                            {t({
                                en: "250 g packs are not available for this pincode — please choose 500 g or 1 kg.",
                                te: "ఈ పిన్‌కోడ్‌కు 250 గ్రా. ప్యాకెట్లు లభించవు — 500 గ్రా. లేదా 1 కేజీ ఎంచుకోండి.",
                            })}
                        </div>
                    )}
                </div>
            )}

            <p className="mt-3 text-[11px] leading-snug text-[color:var(--kp-ink-soft)]" data-testid="free-shipping-note">
                {t({
                    en: "Delivery charges are on us for orders of 1 kg or more — applicable only for Andhra Pradesh and Telangana states.",
                    te: "1 కేజీ లేదా అంతకంటే ఎక్కువ ఆర్డర్లకు డెలివరీ ఛార్జీలు మా భారం — ఆంధ్రప్రదేశ్, తెలంగాణ రాష్ట్రాలకు మాత్రమే వర్తిస్తుంది.",
                })}
            </p>
        </div>
    );
};
